import { useEffect } from 'react';
import LineGraph from './graphLine';
import useRequestState from '../../hooks/useRequestState';
import VarianceIndicator from './varianceIndicator';
import PostHeadlineCardGraph from '../../services/adapters/headlineCardGraph';
import { HeadlineCardInterface } from '../../types/global';
import { CategoricalData } from '../../api-models';
import Description from './description';

import useStreetviewFilterNavigation from '../../hooks/useNavigateUpdateFilters';

interface HeadlineGraphRequest{
  readonly snapshot: string;
  readonly liftDataId: string;
  readonly wardName: string;
}

export default function SummaryHeadlineCard({ headlineCardData, headlineGraphRequest }: Readonly<{
  headlineCardData: HeadlineCardInterface;
  headlineGraphRequest: HeadlineGraphRequest;
}>) {
  const {
    data: headlineCardGraph,
    handleRequest: handleHeadlineCardGraphRequest,
  } = useRequestState<CategoricalData[]>();

  useEffect(() => {
    const getHeadlineCardGraph = async () => {
      const adapter = new PostHeadlineCardGraph();
      const response = adapter.adaptResponse({
        headlineTitle: headlineCardData.title || '',
        snapshot: headlineGraphRequest.snapshot,
        liftDataId: headlineGraphRequest.liftDataId,
        wardName: headlineGraphRequest.wardName
      });
      await handleHeadlineCardGraphRequest(response);
    };
    void getHeadlineCardGraph();
  }, [headlineCardData.title, headlineGraphRequest]);
  const updateFiltersAndRedirect = useStreetviewFilterNavigation();

  return (
    <div className="flex flex-col card justify-between">
      <div id={`summary-headline-card-header-${headlineCardData.title}`} className="p-2">
        <button
          id={`summary-headline-card-header-button-${headlineCardData.title}`}
          type="button"
          onClick={() => updateFiltersAndRedirect(headlineCardData.filters)}
          className="w-full group flex justify-between items-start text-base text-wrap cursor-pointer rounded"
        >
          <span
            title={headlineCardData.title}
            className="text-base text-start text-wrap group-hover:underline group-focus:underline group-hover:text-primary group-focus:text-primary"
          >
            {headlineCardData.title}
          </span>
          <span
            className="text-xl group-hover:text-primary group-focus:text-primary group-hover:scale-110 group-focus:scale-110 transition-transform"
          >
            {headlineCardData.icon}
          </span>
        </button>
      </div>
      <div id={`summary-headline-card-body-${headlineCardData.title}`} className="flex flex-col px-2 pb-2">
        <div
          id="summary-headline-figures-graph-container"
          className="flex sm:flex-row flex-col md:justify-between items-center gap-10"
        >
          <div id="summary-headline-figures-container" className="flex 2xl:flex-row flex-col lg:items-center w-full gap-2">
            <span className="lg:text-2xl text-xl font-semibold">{headlineCardData.value}</span>
            {headlineCardData.variance && headlineCardData.variance !== 'NA'
              ? (<VarianceIndicator variance={headlineCardData.variance} trendUpward={headlineCardData.trend} />) : ''}
          </div>
          <div id="summary-graph-container">
            {
              headlineCardGraph && (
              <LineGraph
                graphData={headlineCardGraph}
                gradient
                showAxis={false}
              />
              )
            }

          </div>
        </div>

      </div>
      <div id={`summary-headline-card-description-${headlineCardData.title}`} className="px-2 pb-2">
        <Description
          description={headlineCardData.description}
          descriptionFilters={headlineCardData.descriptionFilters}
        />
      </div>
    </div>
  );
}
